import * as z from 'zod';
import { usStateOrTerritorySchema, companyCreditReportVendorSchema } from '../../enums.mjs';
import { partialAddressSchema } from '../address/address.mjs';
import { fsr, fein, phoneNumber, zipCode, city, streetAddress } from '../opaque.mjs';
import { currencySchema } from '../utils.mjs';

var Vendor;
(function (Vendor) {
    const experianString = z.coerce.string().nullish().catch(undefined);
    const experianNumber = z.coerce.number().nullish().catch(undefined);
    const experianBoolean = z.coerce.boolean().nullish().catch(undefined);
    const experianObject = (schema) => schema.nullish().catch(undefined);
    const experianArray = (schema) => z.array(schema).nullish().catch(undefined);
    const looseShape = (shape) => z.looseObject(shape);
    Vendor.experianExecutiveElementsSchema = z.object({
        bankruptcyCount: z.number().nullish(),
        earliestBankruptcyDate: z.string().nullish(),
        mostRecentBankruptcyDate: z.string().nullish(),
        taxLienCount: z.number().nullish(),
        earliestTaxLienDate: z.string().nullish(),
        mostRecentTaxLienDate: z.string().nullish(),
        judgmentCount: z.number().nullish(),
        earliestJudgmentDate: z.string().nullish(),
        mostRecentJudgmentDate: z.string().nullish(),
        collectionCount: z.number().nullish(),
        legalBalance: z.number().nullish(),
        uccFilings: z.number().nullish(),
        uccDerogatoryCount: z.number().nullish(),
        currentAccountBalance: z.number().nullish(),
        currentTradelineCount: z.number().nullish(),
        monthlyAverageDbt: z.number().nullish(),
        highestDbt6Months: z.number().nullish(),
        highestDbt5Quarters: z.number().nullish(),
        activeTradelineCount: z.number().nullish(),
        allTradelineBalance: z.number().nullish(),
        allTradelineCount: z.number().nullish(),
        averageBalance5Quarters: z.number().nullish(),
        singleLineHighCredit: z.number().nullish(),
        lowBalance6Months: z.number().nullish(),
        highBalance6Months: z.number().nullish(),
        earliestCollectionDate: z.string().nullish(),
        mostRecentCollectionDate: z.string().nullish(),
        currentDbt: z.number().nullish(),
        yearOfIncorporation: z.string().nullish(),
        taxId: z.string().nullish(),
        earliestUccDate: z.string().nullish(),
        mostRecentUccDate: z.string().nullish(),
        bankruptcyIndicator: z.boolean().nullish(),
        judgmentIndicator: z.boolean().nullish(),
        taxLienIndicator: z.boolean().nullish(),
        inquiriesPrevious9Months: z.number().nullish(),
        lienBalance: z.number().nullish(),
        judgmentBalance: z.number().nullish(),
    });
    Vendor.experianFSRFactorSchema = z.object({
        code: z.string(),
        definition: z.string().nullish(),
    });
    const experianFsrExclusionScore = z.union([z.literal(997), z.literal(998), z.literal(999)]);
    const withoutExperianFsrExclusion = (value) => experianFsrExclusionScore.safeParse(value).success ? undefined : value;
    const fsrScore = z.preprocess(withoutExperianFsrExclusion, fsr.optional());
    const fsrScoreNullish = z.preprocess(withoutExperianFsrExclusion, fsr.nullish());
    const experianTrendScore = z.preprocess(withoutExperianFsrExclusion, z.number().nullish());
    Vendor.experianFSRTrendSchema = z.object({
        quarter: z.string(),
        score: experianTrendScore,
    });
    Vendor.reportSchema = z.object({
        name: z.string().nullish(),
        address: z.object({
            street: streetAddress.nullish(),
            city: city.nullish(),
            state: usStateOrTerritorySchema.nullish(),
            zip: zipCode.nullish(),
        }),
        phone: phoneNumber.nullish(),
        fein: fein.nullish(),
        yearFoundedOrCreditEstablished: z.number().nullish(),
        bankruptcy: z.boolean().nullish(),
        judgmentBalance: currencySchema.nullish(),
        lienBalance: currencySchema.nullish(),
        experianFsr: fsrScoreNullish,
        experianFsrFactors: z.array(Vendor.experianFSRFactorSchema).nullish(),
        experianFsrTrends: z.array(Vendor.experianFSRTrendSchema).nullish(),
        experianBin: z.string().nullish(),
        experianExecutiveElements: Vendor.experianExecutiveElementsSchema.nullish(),
    });
    Vendor.experianPremierProfileAddressSchema = looseShape({
        street: experianString,
        city: experianString,
        state: experianString,
        zip: experianString,
        zipExtension: experianString,
    });
    Vendor.experianPremierProfileBusinessHeaderSchema = looseShape({
        bin: experianString,
        businessName: experianString,
        address: experianObject(Vendor.experianPremierProfileAddressSchema),
        phone: experianString,
        taxId: experianString,
    });
    Vendor.experianPremierProfileBusinessFactsSchema = looseShape({
        fileEstablishedDate: experianString,
        dateOfIncorporation: experianString,
    });
    Vendor.experianPremierProfileExpandedCreditSummarySchema = looseShape({
        bankruptcyCount: experianNumber,
        oldestBankruptcyDate: experianString,
        mostRecentBankruptcyDate: experianString,
        taxLienCount: experianNumber,
        oldestTaxLienDate: experianString,
        mostRecentTaxLienDate: experianString,
        judgmentCount: experianNumber,
        oldestJudgmentDate: experianString,
        mostRecentJudgmentDate: experianString,
        collectionCount: experianNumber,
        legalBalance: experianNumber,
        uccFilings: experianNumber,
        uccDerogatoryCount: experianNumber,
        currentAccountBalance: experianNumber,
        currentTradelineCount: experianNumber,
        monthlyAverageDbt: experianNumber,
        highestDbt6Months: experianNumber,
        highestDbt5Quarters: experianNumber,
        activeTradelineCount: experianNumber,
        allTradelineBalance: experianNumber,
        allTradelineCount: experianNumber,
        averageBalance5Quarters: experianNumber,
        singleHighCredit: experianNumber,
        lowBalance6Months: experianNumber,
        highBalance6Months: experianNumber,
        oldestCollectionDate: experianString,
        mostRecentCollectionDate: experianString,
        currentDbt: experianNumber,
        oldestUccDate: experianString,
        mostRecentUccDate: experianString,
        bankruptcyIndicator: experianBoolean,
        judgmentIndicator: experianBoolean,
        taxLienIndicator: experianBoolean,
    });
    Vendor.experianPremierProfileFsrScoreSchema = looseShape({
        score: experianNumber,
    });
    Vendor.experianPremierProfileScoreFactorSchema = looseShape({
        code: experianString,
        definition: experianString,
    });
    Vendor.experianPremierProfileScoreTrendSchema = looseShape({
        quarter: experianString,
        score: experianNumber,
        industryScore: experianNumber,
    });
    Vendor.experianPremierProfileScoreInformationSchema = looseShape({
        fsrScore: experianObject(Vendor.experianPremierProfileFsrScoreSchema),
        fsrScoreFactors: experianArray(Vendor.experianPremierProfileScoreFactorSchema),
        fsrScoreTrends: experianArray(Vendor.experianPremierProfileScoreTrendSchema),
    });
    Vendor.experianPremierProfileInquiryCountSchema = looseShape({
        month: experianNumber,
        year: experianNumber,
        count: experianNumber,
    });
    Vendor.experianPremierProfileInquirySchema = looseShape({
        inquiryBusinessCategory: experianString,
        inquiryCount: experianArray(Vendor.experianPremierProfileInquiryCountSchema),
    });
    Vendor.experianPremierProfilePublicRecordSchema = looseShape({
        legalAction: experianString,
        liabilityAmount: experianNumber,
    });
    Vendor.experianPremierProfileSchema = looseShape({
        businessHeader: experianObject(Vendor.experianPremierProfileBusinessHeaderSchema),
        businessFacts: experianObject(Vendor.experianPremierProfileBusinessFactsSchema),
        expandedCreditSummary: experianObject(Vendor.experianPremierProfileExpandedCreditSummarySchema),
        inquiries: experianArray(Vendor.experianPremierProfileInquirySchema),
        scoreInformation: experianObject(Vendor.experianPremierProfileScoreInformationSchema),
        taxLienDetail: experianArray(Vendor.experianPremierProfilePublicRecordSchema),
        judgmentDetail: experianArray(Vendor.experianPremierProfilePublicRecordSchema),
    });
    Vendor.experianPremierProfileWriteSchema = z.unknown();
    Vendor.creditReportInputFieldSchema = z.enum(["address"]);
    Vendor.failureDetailSchema = z.object({
        missing: z.array(Vendor.creditReportInputFieldSchema).optional(),
    });
    const creditProfileSharedSchema = {
        failureDetail: Vendor.failureDetailSchema.optional().catch(undefined),
        name: z.string().optional(),
        address: partialAddressSchema.optional(),
        phone: phoneNumber.optional(),
        fein: fein.optional(),
        yearFoundedOrCreditEstablished: z.number().optional(),
        bankruptcy: z.boolean().optional(),
        judgmentBalance: currencySchema.optional(),
        lienBalance: currencySchema.optional(),
        experianFsr: fsrScore,
        experianFsrFactors: z.array(Vendor.experianFSRFactorSchema).optional(),
        experianFsrTrends: z.array(Vendor.experianFSRTrendSchema).optional(),
        experianBin: z.string().optional(),
        experianExecutiveElements: Vendor.experianExecutiveElementsSchema.optional(),
    };
    Vendor.cicCreditProfileSchema = z.object({
        vendor: z.literal(companyCreditReportVendorSchema.enum.cic),
        ...creditProfileSharedSchema,
    });
    Vendor.experianStoredCreditProfileSchema = z.object({
        vendor: z.literal(companyCreditReportVendorSchema.enum.experian),
        ...creditProfileSharedSchema,
        profile: Vendor.experianPremierProfileSchema.optional().catch(undefined),
    });
    Vendor.experianStoredCreditProfileWriteSchema = z.object({
        vendor: z.literal(companyCreditReportVendorSchema.enum.experian),
        ...creditProfileSharedSchema,
        profile: Vendor.experianPremierProfileWriteSchema.optional(),
    });
    Vendor.creditProfileSchema = z.discriminatedUnion("vendor", [
        Vendor.cicCreditProfileSchema,
        Vendor.experianStoredCreditProfileSchema,
    ]);
    Vendor.creditProfileWriteSchema = z.discriminatedUnion("vendor", [
        Vendor.cicCreditProfileSchema,
        Vendor.experianStoredCreditProfileWriteSchema,
    ]);
})(Vendor || (Vendor = {}));

export { Vendor };
